import PropTypes from 'prop-types'
import { InformationCircleIcon } from '@heroicons/react/24/outline'

/**
 * Payment Schedule Preview Component
 * Shows calculated payment schedule for draft contracts
 */
export default function PaymentSchedulePreview({ payments, isPreview = true }) {
  if (!payments || payments.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p className="text-sm">No payment information available</p>
        <p className="text-xs mt-2">
          Enter contract amount, dates, and frequency to see payment schedule
        </p>
      </div>
    )
  }

  const totalAmount = payments.reduce((sum, p) => sum + p.amount, 0)

  return (
    <div className="space-y-4">
      {/* Preview Notice */}
      {isPreview && (
        <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <InformationCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium">Payment Schedule Preview</p>
            <p className="text-blue-700 mt-1">
              These payments will be automatically saved when the contract is signed
            </p>
          </div>
        </div>
      )}

      {/* Payment Table */}
      <div className="overflow-hidden border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                #
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {payments.map((payment) => (
              <tr key={payment.payment_number} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {payment.payment_number}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {new Date(payment.scheduled_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                  €{payment.amount.toLocaleString('en-US', { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                  })}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 border-t-2 border-gray-300">
            <tr>
              <td colSpan="2" className="px-4 py-3 text-sm font-semibold text-gray-900">
                Total ({payments.length} payment{payments.length !== 1 ? 's' : ''})
              </td>
              <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                €{totalAmount.toLocaleString('en-US', { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

PaymentSchedulePreview.propTypes = {
  payments: PropTypes.arrayOf(
    PropTypes.shape({
      payment_number: PropTypes.number.isRequired,
      scheduled_date: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
      status: PropTypes.string
    })
  ),
  isPreview: PropTypes.bool
}